// External libraries
import { Component, createRef } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class CheckboxField extends Component {
  constructor({
    field,
    value,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: JSON.tryParse(value, false),
      valid: this.validate(JSON.tryParse(value, false), properties?.IsRequired),
      invisible: false,
      disabled: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.label = properties?.Label || `${fieldName}:`;
    this.labelVisible = labelVisible;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.checkboxRef = createRef();
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: This field is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.checkboxRef?.current) {
      const inputElement = this.checkboxRef?.current?.querySelector("input");
      if (inputElement) {
        inputElement?.addEventListener("keypress", this.handleKeyPress);
      }
    }
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled
    );
  }

  componentDidUpdate() {
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.required(value) : true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value && entryValues.value[this.fieldName] === "true") {
        this.clearField();
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: false });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: false },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange(e) {
    if (!this.isPrintPreview) {
      this.setState(
        {
          valid: this.validate(e.value, this.isRequired),
          value: e.value,
        },
        () => {
          this.updateFormErrors();
          this.handleFieldChange({
            e,
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            fieldType: this.fieldType,
            eventIndex: this.eventIndex,
          });
          if (this.isStageField) validateStageStep();
        }
      );
    }
  }

  onBlur() {
    this.setState((prevState) => ({
      valid: this.validate(prevState.value, this.isRequired),
    }));
    this.updateFormErrors();
    if (this.isStageField) validateStageStep();
  }

  handleKeyPress = (event) => {
    if (event.key === "Enter") {
      this.onChange({ target: { name: this.fieldName }, value: !this.state.value });
    }
  };
  //#endregion custom functions

  render() {
    if (this.state.invisible) return;
    if (this.isPrintPreview)
      return (
        <div className="k-d-flex k-flex-row k-align-items-center k-font-size-lg">
          {this.labelVisible && <strong>{this.label}</strong>}
          <input
            type="checkbox"
            className="k-h-5 k-w-5"
            checked={this.state.value}
          ></input>
        </div>
      );
    return (
      <Ripple className="!k-d-flex k-font-size-lg !k-font-weight-semibold">
        <div
          className="k-vertical-align-center"
          ref={this.checkboxRef}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              data-testid={this.fieldName}
              id={this.fieldName}
              name={this.fieldName}
              label={this.labelVisible ? this.label : null}
              onChange={(e) => this.onChange(e)}
              onBlur={() => this.onBlur()}
              value={this.state.value}
              checked={this.state.value}
              valid={this.isRequired ? this.state.valid : true}
              required={this.isRequired}
              disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
            />
            <If
              condition={
                this.fieldInformation !== undefined &&
                this.fieldInformation !== "" &&
                this.fieldInformation !== "<p></p>"
              }
            >
              <TooltipPopupIcon content={this.fieldInformation} />
            </If>
          </div>
          <If condition={this.hint}>
            <Hint>{this.hint}</Hint>
          </If>
          <If condition={!this.state.valid}>
            <Error>{this.validationMessage}</Error>
          </If>
        </div>
      </Ripple>
    );
  }
}
